import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Sale {
  id: string;
  sale_date: string;
  total_amount: number;
  quantity_sold: number;
  product_id: string;
  product_name: string;
  unit_price: number;
  payment_method: string;
}

interface SalesTableComparativeStatsProps {
  sales: Sale[];
  customColor?: string;
}

export const SalesTableComparativeStats: React.FC<SalesTableComparativeStatsProps> = ({
  sales,
  customColor
}) => {
  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);

  const calculateStats = useMemo(() => {
    const now = new Date();
    const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

    // Période actuelle (dernier mois)
    const currentPeriodSales = sales.filter(sale => {
      const saleDate = new Date(sale.sale_date);
      return saleDate >= oneMonthAgo && saleDate <= now;
    });

    // 1 mois avant (2 mois à 1 mois avant)
    const oneMonthBeforeSales = sales.filter(sale => {
      const saleDate = new Date(sale.sale_date);
      const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
      return saleDate >= twoMonthsAgo && saleDate < oneMonthAgo;
    });

    // 6 mois avant (7 mois à 6 mois avant)
    const sixMonthsBeforeSales = sales.filter(sale => {
      const saleDate = new Date(sale.sale_date);
      const sevenMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 7, now.getDate());
      return saleDate >= sevenMonthsAgo && saleDate < sixMonthsAgo;
    });

    // 1 an avant (13 mois à 12 mois avant)
    const oneYearBeforeSales = sales.filter(sale => {
      const saleDate = new Date(sale.sale_date);
      const thirteenMonthsAgo = new Date(now.getFullYear() - 1, now.getMonth() - 1, now.getDate());
      return saleDate >= thirteenMonthsAgo && saleDate < oneYearAgo;
    });

    const calculatePeriodStats = (periodSales: Sale[]) => ({
      totalRevenue: periodSales.reduce((sum, sale) => sum + sale.total_amount, 0),
      totalSales: periodSales.length,
      totalItems: periodSales.reduce((sum, sale) => sum + sale.quantity_sold, 0),
      avgOrderValue: periodSales.length > 0 ? periodSales.reduce((sum, sale) => sum + sale.total_amount, 0) / periodSales.length : 0,
      uniqueProducts: new Set(periodSales.map(sale => sale.product_id)).size
    });

    const current = calculatePeriodStats(currentPeriodSales);
    const oneMonthBefore = calculatePeriodStats(oneMonthBeforeSales);
    const sixMonthsBefore = calculatePeriodStats(sixMonthsBeforeSales);
    const oneYearBefore = calculatePeriodStats(oneYearBeforeSales);

    const calculateChange = (current: number, previous: number) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return (current - previous) / previous * 100;
    };

    return {
      current,
      comparisons: [{
        period: '1 mois avant',
        revenue: oneMonthBefore.totalRevenue,
        sales: oneMonthBefore.totalSales,
        items: oneMonthBefore.totalItems,
        avgOrder: oneMonthBefore.avgOrderValue,
        products: oneMonthBefore.uniqueProducts,
        revenueChange: calculateChange(current.totalRevenue, oneMonthBefore.totalRevenue),
        salesChange: calculateChange(current.totalSales, oneMonthBefore.totalSales),
        itemsChange: calculateChange(current.totalItems, oneMonthBefore.totalItems),
        avgOrderChange: calculateChange(current.avgOrderValue, oneMonthBefore.avgOrderValue),
        productsChange: calculateChange(current.uniqueProducts, oneMonthBefore.uniqueProducts)
      }, {
        period: '6 mois avant',
        revenue: sixMonthsBefore.totalRevenue,
        sales: sixMonthsBefore.totalSales,
        items: sixMonthsBefore.totalItems,
        avgOrder: sixMonthsBefore.avgOrderValue,
        products: sixMonthsBefore.uniqueProducts,
        revenueChange: calculateChange(current.totalRevenue, sixMonthsBefore.totalRevenue),
        salesChange: calculateChange(current.totalSales, sixMonthsBefore.totalSales),
        itemsChange: calculateChange(current.totalItems, sixMonthsBefore.totalItems),
        avgOrderChange: calculateChange(current.avgOrderValue, sixMonthsBefore.avgOrderValue),
        productsChange: calculateChange(current.uniqueProducts, sixMonthsBefore.uniqueProducts)
      }, {
        period: '1 an avant',
        revenue: oneYearBefore.totalRevenue,
        sales: oneYearBefore.totalSales,
        items: oneYearBefore.totalItems,
        avgOrder: oneYearBefore.avgOrderValue,
        products: oneYearBefore.uniqueProducts,
        revenueChange: calculateChange(current.totalRevenue, oneYearBefore.totalRevenue),
        salesChange: calculateChange(current.totalSales, oneYearBefore.totalSales),
        itemsChange: calculateChange(current.totalItems, oneYearBefore.totalItems),
        avgOrderChange: calculateChange(current.avgOrderValue, oneYearBefore.avgOrderValue),
        productsChange: calculateChange(current.uniqueProducts, oneYearBefore.uniqueProducts)
      }]
    };
  }, [sales]);

  const getChangeIcon = (change: number) => {
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (change < 0) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">Statistiques comparatives des ventes (dernier mois)</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {calculateStats.comparisons.map((comparison) => (
          <Card key={comparison.period} className="p-4">
            <CardContent className="p-0">
              <h4 className="font-medium text-muted-foreground mb-3">{comparison.period}</h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Chiffre d'affaires</span>
                  <div className="flex items-center gap-2">
                    {getChangeIcon(comparison.revenueChange)}
                    <span className={cn("text-sm font-medium", getChangeColor(comparison.revenueChange))}>
                      {comparison.revenueChange > 0 ? '+' : ''}{comparison.revenueChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Actuel: {formatPrice(calculateStats.current.totalRevenue)} | Avant: {formatPrice(comparison.revenue)}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Nombre de ventes</span>
                  <div className="flex items-center gap-2">
                    {getChangeIcon(comparison.salesChange)}
                    <span className={cn("text-sm font-medium", getChangeColor(comparison.salesChange))}>
                      {comparison.salesChange > 0 ? '+' : ''}{comparison.salesChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Actuel: {calculateStats.current.totalSales} | Avant: {comparison.sales}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Produits différents</span>
                  <div className="flex items-center gap-2">
                    {getChangeIcon(comparison.productsChange)}
                    <span className={cn("text-sm font-medium", getChangeColor(comparison.productsChange))}>
                      {comparison.productsChange > 0 ? '+' : ''}{comparison.productsChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Actuel: {calculateStats.current.uniqueProducts} | Avant: {comparison.products}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Panier moyen</span>
                  <div className="flex items-center gap-2">
                    {getChangeIcon(comparison.avgOrderChange)}
                    <span className={cn("text-sm font-medium", getChangeColor(comparison.avgOrderChange))}>
                      {comparison.avgOrderChange > 0 ? '+' : ''}{comparison.avgOrderChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Actuel: {formatPrice(calculateStats.current.avgOrderValue)} | Avant: {formatPrice(comparison.avgOrder)}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};